import { useRef, useState } from "react";
import { T } from "../tokens";

// Coordinates are stored as percentages (0-100) of the viewer container so
// annotations stay put across 2D/3D toggles and window resizes, without
// needing to reach into PitchCanvas's internal pitch->canvas transform.
export default function AnnotationLayer({ tool, annotations, onAdd, color = "#FFD400" }) {
  const ref = useRef(null);
  const [draft, setDraft] = useState(null); // in-progress arrow/circle/space drag

  const toPct = (e) => {
    const rect = ref.current.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    };
  };

  const handleDown = (e) => {
    if (!tool) return;
    const p = toPct(e);
    if (tool === "note") {
      const text = window.prompt("Note text:");
      if (text) onAdd({ type: "note", x: p.x, y: p.y, text });
      return;
    }
    setDraft({ type: tool, x1: p.x, y1: p.y, x2: p.x, y2: p.y });
  };

  const handleMove = (e) => {
    if (!draft) return;
    const p = toPct(e);
    setDraft(d => ({ ...d, x2: p.x, y2: p.y }));
  };

  const handleUp = () => {
    if (!draft) return;
    // Ignore accidental clicks with no drag distance for shapes that need one
    const dist = Math.hypot(draft.x2 - draft.x1, draft.y2 - draft.y1);
    if (draft.type === "arrow" && dist > 1) onAdd(draft);
    if (draft.type === "circle") onAdd({ ...draft, r: Math.max(dist, 2) });
    if (draft.type === "space") onAdd(draft);
    setDraft(null);
  };

  const live = draft ? [...annotations, draft] : annotations;

  return (
    <svg
      ref={ref}
      onMouseDown={handleDown}
      onMouseMove={handleMove}
      onMouseUp={handleUp}
      onMouseLeave={()=>setDraft(null)}
      style={{
        position: "absolute", inset: 0, width: "100%", height: "100%",
        zIndex: 8, cursor: tool ? "crosshair" : "default",
        pointerEvents: tool ? "auto" : "none",
      }}
    >
      <defs>
        <marker id="wb-arrowhead" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
          <path d="M0,0 L8,4 L0,8 Z" fill={color} />
        </marker>
      </defs>
      {live.map((a, i) => {
        if (a.type === "arrow") return (
          <line key={i} x1={`${a.x1}%`} y1={`${a.y1}%`} x2={`${a.x2}%`} y2={`${a.y2}%`}
            stroke={color} strokeWidth={2.5} markerEnd="url(#wb-arrowhead)" opacity={0.9}/>
        );
        if (a.type === "circle") return (
          <circle key={i} cx={`${a.x1}%`} cy={`${a.y1}%`} r={`${a.r}%`}
            fill="none" stroke={color} strokeWidth={2} strokeDasharray="4 3" opacity={0.9}/>
        );
        if (a.type === "space") {
          const x = Math.min(a.x1, a.x2), y = Math.min(a.y1, a.y2);
          const w = Math.abs(a.x2 - a.x1), h = Math.abs(a.y2 - a.y1);
          return (
            <rect key={i} x={`${x}%`} y={`${y}%`} width={`${w}%`} height={`${h}%`}
              fill={`${color}22`} stroke={color} strokeWidth={1.5} opacity={0.9}/>
          );
        }
        if (a.type === "note") return (
          <g key={i}>
            <foreignObject x={`${a.x}%`} y={`${a.y}%`} width="140" height="40" style={{overflow:"visible"}}>
              <div style={{
                background: T.panel, border: `1px solid ${color}`, borderRadius: 5,
                color: T.textPri, fontSize: 10, padding: "3px 7px",
                fontFamily: "'Inter',sans-serif", maxWidth: 130, whiteSpace: "normal",
              }}>{a.text}</div>
            </foreignObject>
          </g>
        );
        return null;
      })}
    </svg>
  );
}
